import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Layout,
  message,
  Row,
  Select,
  Upload,
} from "antd";
import {
  Trash,
  Plus,
  Inbox,
  Eye,
  ExternalLink,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import ConfirmAction from "../../Components/ConfirmAction/ConfirmAction";
import ReasonModal from "../../Components/CustomModals/ReasonModal";
import ViewAttachmentModal from "../../Components/CustomModals/ViewAttachmentModal";
import {
  formatNumbers,
  formatString,
  navigateTo,
} from "../../Utils/Common";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useParams } from "react-router-dom";

const { Dragger } = Upload;
const { Option } = Select;

type SourceType = "purchase_order" | "petty_cash";

interface SourceConfig {
  fetchEndpoint: string;
  fetchParams: Record<string, any>;
  idColumnLabel: string;
  refIdColumnLabel: string;
  dateColumnLabel: string;
  payeeColumnLabel: string;
  listLabel: string;
  viewRoute: string;
  /** Normalize API row into the shared line-item shape used by the form. */
  normalize: (row: any) => {
    id: any;
    ref_id: any;
    expense_date: any;
    payee_name: string;
    branch_name: string;
    grand_total: any;
  };
}

const SOURCE_CONFIG: Record<SourceType, SourceConfig> = {
  purchase_order: {
    fetchEndpoint: "purchase_orders/get",
    fetchParams: { status: "approved", created_payment_request: 0 },
    idColumnLabel: "Purchase Order #",
    refIdColumnLabel: "Purchase Request #",
    dateColumnLabel: "Transaction Date",
    payeeColumnLabel: "Supplier Name",
    listLabel: "List of Purchase Orders:",
    viewRoute: "/purchase-order/view",
    normalize: (row: any) => ({
      id: row.id ?? row.purchase_order_id,
      ref_id: row.purchase_request_id,
      expense_date: row.expense_date ?? row.date,
      payee_name: row.supplier_name ?? "",
      branch_name: row.branch_name ?? "",
      grand_total: row.grand_total,
    }),
  },
  petty_cash: {
    fetchEndpoint: "petty_cash_requisitions/get",
    fetchParams: { status: "approved" },
    idColumnLabel: "Petty Cash #",
    refIdColumnLabel: "PC No.",
    dateColumnLabel: "Requisition Date",
    payeeColumnLabel: "Supplier / Payee",
    listLabel: "List of Petty Cash (Liquidation):",
    viewRoute: "/petty-cash-requisition/view",
    normalize: (row: any) => ({
      id:
        row.petty_cash_requisition_id ??
        row.source_document_id ??
        row.id ??
        row.purchase_order_id,
      ref_id: row.pc_no ?? row.ref_id ?? row.id,
      expense_date: row.date ?? row.expense_date,
      payee_name: row.supplier_name || row.requested_by || row.added_by_name || "",
      branch_name: row.branch_name ?? "",
      grand_total: row.grand_total,
    }),
  },
};

const SOURCE_TYPE_LABELS: Record<SourceType, string> = {
  purchase_order: "Purchase Order",
  petty_cash: "Petty Cash",
};

interface PaymentRequestFormProps {
  add?: boolean;
  edit?: boolean;
  view?: boolean;
  review?: boolean;
  approval?: boolean;
}

const PaymentRequestForm: React.FC<PaymentRequestFormProps> = ({
  add,
  edit,
  view,
  review,
  approval,
}) => {
  const [loading, setLoading] = useState("");
  const [showDisapproveModal, setShowDisapproveModal] = useState(false);
  const [disapprovalReason, setDisapprovalReason] = useState("");
  const [sourceType, setSourceType] = useState<SourceType>("purchase_order");
  const [sourceItems, setSourceItems] = useState<any[]>([]);
  const [form] = Form.useForm();
  const [attachmentUrls, setAttachmentUrls] = useState<string[]>([]);
  const [showAttachment, setShowAttachment] = useState<string | null>(null);

  const itemsArray = Form.useWatch("itemsArray", form);
  const { id: payment_request_id } = useParams<{ id: string }>();

  const isReadOnly = view || review || approval;
  const canChangeSourceType = !!add;
  const config = SOURCE_CONFIG[sourceType];

  function clearFields() {
    form.resetFields();
  }

  const grandTotal = (itemsArray || []).reduce((sum: number, item: any) => {
    return sum + (Number(item?.grand_total) || 0);
  }, 0);

  const handleBeforeUpload = (file: any) => {
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) message.error(`${file.name} exceeds the 2MB size limit.`);
    return false;
  };

  const handleUploadChange = (info: any) => {
    const validFiles = info.fileList
      .filter((f: any) => f.size / 1024 / 1024 < 2)
      .slice();
    form.setFieldValue("attachment", validFiles);
  };

  function dedupeItemsBySourceId(rows: any[] = []) {
    const seen = new Set<string>();
    return rows.filter((row) => {
      const id = toSourceId(row?.source_id ?? row?.id);
      if (id == null || id === "") return true;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }

  async function submitForm(data: any) {
    const uniqueItems = dedupeItemsBySourceId(data.itemsArray || []);
    const formattedItemsArray = uniqueItems.map((item: any) => ({
      ...item,
      expense_date: item.expense_date
        ? dayjs(item.expense_date).format("YYYY-MM-DD")
        : "",
    }));

    const source_ids = uniqueItems
      .map((item: any) => item.source_id)
      .filter(Boolean);

    const uniqueGrandTotal = uniqueItems.reduce(
      (sum: number, item: any) => sum + (Number(item?.grand_total) || 0),
      0
    );

    const formData = new FormData();
    formData.append("itemsArray", JSON.stringify(formattedItemsArray));
    formData.append("grand_total", String(uniqueGrandTotal));
    formData.append("source_type", sourceType);
    formData.append("source_ids", JSON.stringify(source_ids));
    // Backend update/create order linking still uses this key for both sources.
    formData.append("purchase_order_ids", JSON.stringify(source_ids));

    if (edit) formData.append("payment_request_id", String(payment_request_id));

    const attachments = data.attachment || [];
    attachments.forEach((f: any) => {
      if (f.originFileObj) formData.append("attachment[]", f.originFileObj);
    });

    setLoading("submit");
    const endpoint = edit
      ? "payment_requests/update"
      : "payment_requests/create";
    const response = await postRequest(endpoint, formData, true);

    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setLoading("");
        navigateTo("/payment-request");
      }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${edit ? "update" : "create"} payment request.`
      );
    }
  }

  async function submitApprove() {
    setLoading("approve");
    const response = await postRequest(
      "payment_requests/change_status",
      { payment_request_id, action: "approved" },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setLoading("");
        navigateTo("/payment-request");
      }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to approve payment request."
      );
    }
  }

  async function submitCheck() {
    setLoading("check");
    const response = await postRequest(
      "payment_requests/change_status",
      { payment_request_id, action: "checked" },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setLoading("");
        navigateTo("/payment-request");
      }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to check payment request."
      );
    }
  }

  async function submitDisapprove() {
    if (!disapprovalReason.trim()) {
      message.error("Please provide a reason for disapproval.");
      return;
    }
    setShowDisapproveModal(false);
    setLoading("disapprove");
    const response = await postRequest(
      "payment_requests/change_status",
      {
        payment_request_id,
        action: "disapproved",
        reason: disapprovalReason,
      },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setLoading("");
        navigateTo("/payment-request");
      }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to disapprove payment request."
      );
    }
  }

  function toSourceId(value: any) {
    if (value == null || value === "") return value;
    return String(value);
  }

  function mergeSourceItems(available: any[], linked: any[] = []) {
    const byId = new Map<string, any>();
    [...available, ...linked].forEach((item) => {
      const id = toSourceId(item?.id);
      if (id == null || id === "" || byId.has(id)) return;
      byId.set(id, { ...item, id });
    });
    return Array.from(byId.values());
  }

  function normalizeOrderRow(cfg: SourceConfig, order: any) {
    return cfg.normalize({
      ...order,
      id: toSourceId(
        order.petty_cash_requisition_id ??
          order.source_document_id ??
          order.source_id ??
          order.purchase_order_id ??
          order.id
      ),
      purchase_order_id:
        order.petty_cash_requisition_id ??
        order.source_document_id ??
        order.purchase_order_id ??
        order.source_id,
      purchase_request_id:
        order.purchase_request_id ?? order.ref_id ?? order.pc_no,
      supplier_name: order.supplier_name ?? order.payee_name,
      date: order.date ?? order.expense_date,
      pc_no: order.pc_no ?? order.ref_id,
    });
  }

  function isLinkedItemIncomplete(item: any) {
    const hasAmount =
      item?.grand_total != null &&
      item?.grand_total !== "" &&
      !Number.isNaN(Number(item.grand_total));
    const hasDate = Boolean(item?.expense_date);
    const hasPayee = Boolean(item?.payee_name);
    // PC No. falling back to the same id means detail join likely failed.
    const refLooksLikeId =
      item?.ref_id != null &&
      toSourceId(item.ref_id) === toSourceId(item.id);
    return !hasAmount || !hasDate || !hasPayee || refLooksLikeId;
  }

  async function hydrateLinkedSourceDetails(
    type: SourceType,
    linkedItems: any[]
  ) {
    const cfg = SOURCE_CONFIG[type];
    const hydrated = await Promise.all(
      linkedItems.map(async (item) => {
        if (!item?.id || !isLinkedItemIncomplete(item)) {
          return { ...item, id: toSourceId(item.id) };
        }

        const params =
          type === "petty_cash"
            ? { petty_cash_requisition_id: item.id }
            : { purchase_order_id: item.id };

        const response = await getRequest(cfg.fetchEndpoint, params, true);
        const row = response.success
          ? (Array.isArray(response.data.data)
              ? response.data.data[0]
              : null)
          : null;

        if (!row) {
          return { ...item, id: toSourceId(item.id) };
        }

        const normalized = cfg.normalize(row);
        return {
          ...normalized,
          id: toSourceId(normalized.id ?? item.id),
        };
      })
    );

    return hydrated;
  }

  async function fetchSourceItems(type: SourceType, linkedItems: any[] = []) {
    const cfg = SOURCE_CONFIG[type];
    setLoading("fetch");
    const response = await getRequest(
      cfg.fetchEndpoint,
      cfg.fetchParams,
      true
    );
    const available = response.success
      ? (Array.isArray(response.data.data) ? response.data.data : []).map(
          (row: any) => {
            const normalized = cfg.normalize(row);
            return { ...normalized, id: toSourceId(normalized.id) };
          }
        )
      : [];
    // Keep already-linked rows (e.g. completed petty cash / used POs)
    // so Edit/View Select can still render the saved source_id.
    setSourceItems(mergeSourceItems(available, linkedItems));
    setLoading("");
  }

  async function fetchPaymentRequestData() {
    if (!payment_request_id) return;
    setLoading("fetch");
    const response = await getRequest(
      "payment_requests/get",
      { payment_request_id },
      true
    );
    if (response.success) {
      const data = response.data.data[0];
      const loadedSourceType: SourceType =
        data.source_type === "petty_cash"
          ? "petty_cash"
          : "purchase_order";
      const cfg = SOURCE_CONFIG[loadedSourceType];

      const sourceRows = data.orders || data.items || data.sources || [];
      let linkedItems = (sourceRows || []).map((order: any) =>
        normalizeOrderRow(cfg, order)
      );
      // Orders API may return only purchase_order_id without joined
      // date/payee/amount — fetch full source docs so Edit can show them.
      linkedItems = await hydrateLinkedSourceDetails(
        loadedSourceType,
        linkedItems
      );
      // Guard against duplicate payment_order_request rows / Form.List init bugs.
      linkedItems = dedupeItemsBySourceId(linkedItems);

      const items = linkedItems.map((normalized: any) => ({
        source_id: toSourceId(normalized.id),
        ref_id: normalized.ref_id,
        expense_date: normalized.expense_date
          ? dayjs(normalized.expense_date)
          : null,
        payee_name: normalized.payee_name,
        branch_name: normalized.branch_name,
        grand_total: normalized.grand_total,
      }));

      // Set source type before fields so labels/config match the loaded rows.
      setSourceType(loadedSourceType);
      // Replace list entirely (avoid Form.List appending onto initial [{}]).
      form.setFieldsValue({
        source_type: loadedSourceType,
        itemsArray: items.length ? items : [{}],
      });

      if (add || edit) {
        await fetchSourceItems(loadedSourceType, linkedItems);
      } else {
        setSourceItems(linkedItems);
      }

      if (Array.isArray(data.attachments)) {
        setAttachmentUrls(data.attachments);
      } else if (data.attachment_url) {
        setAttachmentUrls([data.attachment_url]);
      } else {
        setAttachmentUrls([]);
      }
    }
    setLoading("");
  }

  function handleSelectSourceItem(selectedId: any, fieldName: any) {
    const selectedItem = sourceItems.find(
      (item: any) => toSourceId(item.id) === toSourceId(selectedId)
    );
    if (!selectedItem) return;
    form.setFields([
      {
        name: ["itemsArray", fieldName, "ref_id"],
        value: selectedItem.ref_id,
      },
      {
        name: ["itemsArray", fieldName, "expense_date"],
        value: selectedItem.expense_date
          ? dayjs(selectedItem.expense_date)
          : null,
      },
      {
        name: ["itemsArray", fieldName, "payee_name"],
        value: selectedItem.payee_name,
      },
      {
        name: ["itemsArray", fieldName, "branch_name"],
        value: selectedItem.branch_name,
      },
      {
        name: ["itemsArray", fieldName, "grand_total"],
        value: selectedItem.grand_total,
      },
    ]);
  }

  function handleClearSourceItem(fieldName: any) {
    form.setFields([
      { name: ["itemsArray", fieldName, "ref_id"], value: "" },
      { name: ["itemsArray", fieldName, "expense_date"], value: null },
      { name: ["itemsArray", fieldName, "payee_name"], value: "" },
      { name: ["itemsArray", fieldName, "branch_name"], value: "" },
      { name: ["itemsArray", fieldName, "grand_total"], value: null },
    ]);
  }

  function handleSourceTypeChange(nextType: SourceType) {
    setSourceType(nextType);
    form.setFieldsValue({
      source_type: nextType,
      itemsArray: [{}],
    });
  }

  useEffect(() => {
    if (view || review || approval || edit) {
      fetchPaymentRequestData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add-only: reload available sources when user switches source type.
  // Edit/view load linked rows inside fetchPaymentRequestData so we don't
  // wipe completed/used sources from the Select options.
  useEffect(() => {
    if (add) {
      fetchSourceItems(sourceType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceType]);

  const pageTitle = add
    ? "Add Payment Request"
    : edit
      ? "Edit Payment Request"
      : review
        ? "Check Payment Request"
        : approval
          ? "Approve Payment Request"
          : "View Payment Request";

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title={pageTitle}
              withDateRangeFilter={false}
              withAddButton={false}
              withSearchBtn={false}
            />
          </Col>

          <div className="d-flex w-full form-container">
            <Col span={24}>
              <Form
                form={form}
                onFinish={submitForm}
                layout="vertical"
                initialValues={{ source_type: sourceType, itemsArray: [{}] }}
              >
                <Row className="w-full" gutter={[10, 10]}>
                  <Col xs={24} md={12} lg={8}>
                    <Form.Item
                      label="Source Type"
                      name="source_type"
                      rules={[
                        {
                          required: true,
                          message: "Please select a source type.",
                        },
                      ]}
                    >
                      <Select
                        disabled={!canChangeSourceType}
                        onChange={handleSourceTypeChange}
                      >
                        <Option value="purchase_order">
                          {SOURCE_TYPE_LABELS.purchase_order}
                        </Option>
                        <Option value="petty_cash">
                          {SOURCE_TYPE_LABELS.petty_cash}
                        </Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <span className="edit-label mb-2">
                      <label className="text-red-500 me-1">*</label>
                      {config.listLabel}
                    </span>
                    <Form.List name="itemsArray">
                      {(fields, { add: addItem, remove }) => (
                        <>
                          <Col xs={24}>
                            <div className="overflow-auto max-h-[600px]">
                              <table className="w-full border border-collapse min-w-[800px]">
                                <thead>
                                  <tr className="bg-primary text-white font-bold border">
                                    <th className="p-2 border w-[120px]">
                                      {config.idColumnLabel}
                                    </th>
                                    <th className="p-2 border w-[120px]">
                                      {config.refIdColumnLabel}
                                    </th>
                                    <th className="p-2 border w-[150px]">
                                      {config.dateColumnLabel}
                                    </th>
                                    <th className="p-2 border w-[150px]">
                                      {config.payeeColumnLabel}
                                    </th>
                                    <th className="p-2 border w-[150px]">
                                      Branch
                                    </th>
                                    <th className="p-2 border w-[120px]">
                                      Amount Payable
                                    </th>
                                    <th className="p-2 border w-[80px] text-center">
                                      Action
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {fields.map((field) => (
                                    <tr
                                      key={field.key}
                                      className="border bg-white"
                                    >
                                      <td className="p-2 border">
                                        <Form.Item
                                          name={[field.name, "source_id"]}
                                          rules={[
                                            {
                                              required: true,
                                              message: "Required",
                                            },
                                          ]}
                                          noStyle
                                        >
                                          <Select
                                            className="w-full"
                                            placeholder={`Select ${config.idColumnLabel}`}
                                            showSearch
                                            allowClear
                                            disabled={isReadOnly}
                                            loading={loading === "fetch"}
                                            onSelect={(value) =>
                                              handleSelectSourceItem(
                                                value,
                                                field.name
                                              )
                                            }
                                            onClear={() =>
                                              handleClearSourceItem(field.name)
                                            }
                                            filterOption={(
                                              input: string,
                                              option?: any
                                            ) =>
                                              String(option?.children ?? "")
                                                .toLowerCase()
                                                .includes(input.toLowerCase())
                                            }
                                          >
                                            {sourceItems
                                              .filter((item: any) => {
                                                const itemId = toSourceId(
                                                  item.id
                                                );
                                                return !form
                                                  .getFieldValue("itemsArray")
                                                  ?.some(
                                                    (
                                                      row: any,
                                                      index: number
                                                    ) =>
                                                      toSourceId(
                                                        row?.source_id
                                                      ) === itemId &&
                                                      index !== field.name
                                                  );
                                              })
                                              .map((item: any) => (
                                                <Select.Option
                                                  key={toSourceId(item.id)}
                                                  value={toSourceId(item.id)}
                                                >
                                                  {toSourceId(item.id)}
                                                </Select.Option>
                                              ))}
                                          </Select>
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item
                                          name={[field.name, "ref_id"]}
                                          noStyle
                                        >
                                          <Input disabled />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item
                                          name={[field.name, "expense_date"]}
                                          noStyle
                                        >
                                          <DatePicker
                                            className="w-full"
                                            disabled
                                          />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item
                                          name={[field.name, "payee_name"]}
                                          noStyle
                                        >
                                          <Input disabled />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item
                                          name={[field.name, "branch_name"]}
                                          noStyle
                                        >
                                          <Input disabled />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item
                                          name={[field.name, "grand_total"]}
                                          noStyle
                                          getValueProps={(value) => ({
                                            value:
                                              value == null || value === ""
                                                ? ""
                                                : formatNumbers(value),
                                          })}
                                        >
                                          <Input disabled />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border text-center">
                                        {form.getFieldValue("itemsArray")?.[
                                          field.name
                                        ]?.source_id && (
                                          <Button
                                            type="text"
                                            icon={
                                              <Eye
                                                color="#557ac3"
                                                size={17}
                                              />
                                            }
                                            onClick={() => {
                                              const sourceId =
                                                form.getFieldValue(
                                                  "itemsArray"
                                                )?.[field.name]?.source_id;
                                              window.open(
                                                `${config.viewRoute}/${sourceId}`,
                                                "_blank"
                                              );
                                            }}
                                          />
                                        )}
                                        {!isReadOnly &&
                                          fields.length > 1 && (
                                            <Button
                                              type="text"
                                              danger
                                              icon={<Trash size={17} />}
                                              onClick={() =>
                                                remove(field.name)
                                              }
                                            />
                                          )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Col>

                          <Col xs={24} className="mt-2">
                            <Row justify="space-between" align="middle">
                              <Col>
                                {!isReadOnly && (
                                  <Button
                                    type="primary"
                                    onClick={() => addItem({})}
                                    icon={<Plus size={16} />}
                                  >
                                    Add {config.idColumnLabel}
                                  </Button>
                                )}
                              </Col>
                              <Col className="text-end">
                                <strong>
                                  Grand Total: ₱ {formatNumbers(grandTotal)}
                                </strong>
                              </Col>
                            </Row>
                          </Col>
                        </>
                      )}
                    </Form.List>
                  </Col>

                  {(isReadOnly || edit) && attachmentUrls.length > 0 && (
                    <Col xs={24} className="pt-1 mt-4">
                      <span className="font-bold block mb-2">
                        Attachment(s)
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {attachmentUrls.map((url, index) => (
                          <Button
                            key={index}
                            icon={<ExternalLink size={14} />}
                            onClick={() => setShowAttachment(url)}
                          >
                            Attachment {index + 1}
                          </Button>
                        ))}
                      </div>
                    </Col>
                  )}

                  {(add || edit) && (
                    <Col xs={24} className="mt-5 pt-5">
                      <Form.Item
                        label="Attachment(s)"
                        name="attachment"
                        valuePropName="fileList"
                        getValueFromEvent={(e) =>
                          Array.isArray(e) ? e : e?.fileList
                        }
                      >
                        <Dragger
                          name="file"
                          multiple
                          beforeUpload={handleBeforeUpload}
                          onChange={handleUploadChange}
                          accept=".pdf,image/*"
                        >
                          <p className="ant-upload-drag-icon flex justify-center items-center text-3xl">
                            <Inbox />
                          </p>
                          <p className="ant-upload-text">
                            Click or drag files to upload
                          </p>
                          <p className="ant-upload-hint">
                            Supports PDF, Image formats. You can upload multiple
                            files.
                          </p>
                        </Dragger>
                        <p className="text-textColor pt-2">
                          Note: Maximum file size is 2MB per file.
                        </p>
                      </Form.Item>
                    </Col>
                  )}

                  <Col lg={24} className="mt-5 flex justify-end gap-3">
                    <Button
                      onClick={() => {
                        navigateTo("/payment-request");
                        clearFields();
                      }}
                    >
                      {view || review || approval ? "Close" : "Cancel"}
                    </Button>

                    {(add || edit) && (
                      <Button
                        htmlType="submit"
                        type="primary"
                        loading={loading === "submit"}
                      >
                        {edit ? "Update" : "Submit"}
                      </Button>
                    )}

                    {review && (
                      <>
                        <Button
                          danger
                          loading={loading === "disapprove"}
                          onClick={() => {
                            setDisapprovalReason("");
                            setShowDisapproveModal(true);
                          }}
                        >
                          Disapprove
                        </Button>
                        <ConfirmAction
                          title="Are you sure you want to mark this payment request as checked?"
                          onConfirm={submitCheck}
                          okText="Check"
                          cancelText="Cancel"
                          okButtonProps={{ loading: loading === "check" }}
                        >
                          <Button type="primary" loading={loading === "check"}>
                            Check
                          </Button>
                        </ConfirmAction>
                      </>
                    )}

                    {approval && (
                      <>
                        <Button
                          danger
                          loading={loading === "disapprove"}
                          onClick={() => {
                            setDisapprovalReason("");
                            setShowDisapproveModal(true);
                          }}
                        >
                          Disapprove
                        </Button>
                        <ConfirmAction
                          title="Are you sure you want to approve this payment request?"
                          onConfirm={submitApprove}
                          okText="Approve"
                          cancelText="Cancel"
                          okButtonProps={{ loading: loading === "approve" }}
                        >
                          <Button
                            type="primary"
                            loading={loading === "approve"}
                          >
                            Approve
                          </Button>
                        </ConfirmAction>
                      </>
                    )}
                  </Col>
                </Row>
              </Form>
            </Col>
          </div>
        </Row>
      </Layout>

      <ViewAttachmentModal
        showModal={!!showAttachment}
        attachmentUrl={showAttachment ?? ""}
        onCancel={() => setShowAttachment(null)}
      />

      <ReasonModal
        showModal={showDisapproveModal}
        onCancel={() => setShowDisapproveModal(false)}
        title="Please provide a reason for disapproval:"
        reasonValue={disapprovalReason}
        onReasonChange={(e: any) => setDisapprovalReason(e.target.value)}
        footer={[
          <Button key="cancel" onClick={() => setShowDisapproveModal(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={loading === "disapprove"}
            disabled={!disapprovalReason.trim()}
            onClick={submitDisapprove}
          >
            Confirm Disapprove
          </Button>,
        ]}
      />

      <PersistentFooter />
    </Layout>
  );
};

export default PaymentRequestForm;
